import { LinkedinRequest, LinkedinRequestResponse } from '../linkedin-request.abstract';
import dayjs from 'dayjs';

export interface LinkedinPostParsed {
    postId: string;
    authorMemberId: string;
    contentText: string;
    postedOn?: Date;
    article?: {
        text: string;
        link: string;
    };
    numberOfComments?: number;
    numberOfReactions?: number;
    ugcUrn?: string;
    images: string[];
    video: string | undefined;
    repost?: {
        postId: string;
        postedOn?: Date;
        contentText: string;
        article?: {
            text: string;
            link: string;
        };
        images: string[];
        video: string | undefined;
    };
}

export class GetPostsLinkedinRequest extends LinkedinRequest<{ memberId: string }, LinkedinPostParsed[]> {
    constructor(args: { memberId: string }) {
        super(args);
    }

    createRequest() {
        const queryParams = `includeWebMetadata=true&variables=(count:20,start:0,profileUrn:urn%3Ali%3Afsd_profile%3A${this.args.memberId})&queryId=voyagerFeedDashProfileUpdates.cc049925968b824f53bf19b4aeccc365`;
        return {
            fullUrl: `https://www.linkedin.com/voyager/api/graphql?${queryParams}`,
            method: 'GET',
            headers: {
                'x-restli-protocol-version': '2.0.0',
            },
            body: null,
            queryParams,
        };
    }

    parseInternal(
        response: LinkedinRequestResponse,
    ): { result: LinkedinPostParsed[]; success: true } | { error: any; success: false } {
        if (response.status === 403 || response.status === 404) {
            return { error: 'profile_unavailable', success: false };
        }
        try {
            const result = JSON.parse(response.text);
            return { result: resultDataParser(result, this.args.memberId), success: true };
        } catch (error) {
            return { error: error, success: false };
        }
    }
}

const getElementsPostedByUser = (result: RootObject): Included[] => {
    const urns = result.data.data.feedDashProfileUpdatesByMemberShareFeed['*elements'];
    const elements = result.included.filter((el) => urns.includes(el.entityUrn));

    // Sort descending
    elements.sort((a, b) => -a.entityUrn.localeCompare(b.entityUrn));
    return elements;
};

const parsePostDate = (postedOnString: string): Date | undefined => {
    // First try to parse relative time (e.g. "2 days ago")
    if (postedOnString.includes('ago')) {
        return dayjs()
            .subtract(
                parseInt(postedOnString.split(' ')[0]),
                postedOnString.split(' ')[1] as
                    | 'weeks'
                    | 'week'
                    | 'days'
                    | 'day'
                    | 'hours'
                    | 'hour'
                    | 'minutes'
                    | 'minute',
            )
            .toDate();
    }

    return undefined;
};

const extractPostIdFromUrn = (urn: string) => {
    const match = urn.match(/urn:li:activity:(.+?),/);
    return match ? match[1] : null;
};
const extractImagesFromPost = (post: Included) => {
    const images: string[] = [];
    if (post.content?.imageComponent) {
        for (const image of post.content.imageComponent.images) {
            for (const attribute of image.attributes) {
                const vectorImage = attribute?.detailData?.vectorImage;
                if (vectorImage) {
                    const desiredSize =
                        vectorImage.artifacts.find((artifact) => artifact.width === 400) ||
                        vectorImage.artifacts.find((artifact) => artifact.width > 200 && artifact.width < 1000) ||
                        vectorImage.artifacts[0];

                    if (desiredSize) {
                        images.push(vectorImage.rootUrl + desiredSize.fileIdentifyingUrlPathSegment);
                    }
                }
            }
        }
    }
    return images;
};

const extractVideosFromPost = (post: Included, result: RootObject): string | undefined => {
    const videoUrn = post.content?.linkedInVideoComponent?.['*videoPlayMetadata'];
    if (videoUrn) {
        const videoItem = result.included.find((el) => el.entityUrn === videoUrn);
        if (videoItem) {
            const video = videoItem.progressiveStreams?.[0];
            if (video) {
                return video?.streamingLocations[0]?.url;
            }
        }
    }
    return undefined;
};

const extractReactionsFromPost = (postId: string, result: RootObject, ugcUrn: string | undefined) => {
    const reactionsObject = result.included.find((el) => {
        return el.entityUrn === `urn:li:fsd_socialActivityCounts:${ugcUrn ? ugcUrn : 'urn:li:activity:' + postId}`;
    });
    if (reactionsObject) {
        const numReactions = reactionsObject.numLikes;
        const numComments = reactionsObject.numComments;
        return {
            numberOfReactions: numReactions,
            numberOfComments: numComments,
        };
    }
    return {
        numberOfReactions: undefined,
        numberOfComments: undefined,
    };
};

const resultDataParser = (result: RootObject, memberId: string): LinkedinPostParsed[] => {
    const parsedPosts: LinkedinPostParsed[] = [];

    const usersPostsElements = getElementsPostedByUser(result);

    for (const post of usersPostsElements) {
        const postId = extractPostIdFromUrn(post.entityUrn);

        if (!postId) {
            continue;
        }

        const ugcUrn = post.metadata?.shareUrn?.includes('ugcPost') ? post.metadata?.shareUrn : undefined;

        const repostUrn = post['*resharedUpdate'];
        const repost = repostUrn ? result.included.find((el) => el.entityUrn === repostUrn) : undefined;
        const authorName = post.actor?.name.text;
        const postedOnString = post.actor?.subDescription.accessibilityText;
        const reactions = extractReactionsFromPost(postId, result, ugcUrn);

        if (!authorName || !postedOnString) {
            continue;
        }

        if (!repost && post?.header?.text?.text?.includes('reposted')) {
            // This is the case where the person has reposted without adding any text, which we will ignore for now
            continue;
        }

        const parsedPost: LinkedinPostParsed = {
            postId,
            authorMemberId: memberId,
            // url: 'https://www.linkedin.com/feed/update/urn:li:activity:' + postId,
            // author: post.actor?.name.text ?? '',
            postedOn: parsePostDate(post.actor?.subDescription.accessibilityText ?? ''),
            contentText: post.commentary?.text?.text ?? '',
            article: post.content?.articleComponent
                ? {
                      text: post.content.articleComponent.title?.text,
                      link: post.content.articleComponent.navigationContext?.actionTarget,
                  }
                : undefined,
            numberOfComments: reactions.numberOfComments,
            numberOfReactions: reactions.numberOfReactions,
            ugcUrn: ugcUrn,
            images: extractImagesFromPost(post),
            video: extractVideosFromPost(post, result),
            repost: repost
                ? {
                      postId: extractPostIdFromUrn(repost.entityUrn) as string,
                      //   url:
                      //       'https://www.linkedin.com/feed/update/urn:li:activity:' +
                      //       extractPostIdFromUrn(repost.entityUrn),
                      //   author: repost.actor?.name.text ?? '',
                      postedOn: parsePostDate(repost.actor?.subDescription.accessibilityText ?? ''),
                      contentText: repost.commentary?.text?.text ?? '',
                      article: repost.content?.articleComponent
                          ? {
                                text: repost.content.articleComponent.title?.text,
                                link: repost.content.articleComponent.navigationContext?.actionTarget,
                            }
                          : undefined,
                      images: extractImagesFromPost(repost),
                      video: extractVideosFromPost(repost, result),
                  }
                : undefined,
        };

        parsedPosts.push(parsedPost);
    }

    return parsedPosts;
};

interface RootObject {
    data: Data2;
    included: Included[];
}

interface Included {
    thumbnail?: Thumbnail;
    progressiveStreams?: ProgressiveStream[];
    liveStreamCreatedAt?: null;
    transcripts?: any[];
    prevMedia?: null;
    aspectRatio?: number;
    media?: string;
    c2paManifestData?: null;
    $recipeTypes: string[];
    adaptiveStreams?: AdaptiveStream[];
    liveStreamEndedAt?: null;
    $type: string;
    duration?: number;
    entityUrn: string;
    provider?: string;
    nextMedia?: null;
    thumbnails?: null;
    trackingId?: string;
    trackingUrn?: string;
    actionTarget?: string;
    saved?: boolean;
    preDashEntityUrn?: string;
    reactionByOrganizationActor?: null;
    numLikes?: number;
    liked?: boolean;
    urn?: string;
    numComments?: number;
    highlightedReactorName?: HighlightedReactorName | null;
    reactionTypeCounts?: ReactionTypeCount[];
    numShares?: number;
    numImpressions?: null | number;
    reacted?: null | string;
    metadata?: Metadata2;
    socialContent?: SocialContent;
    showSocialDetail?: boolean;
    footer?: null;
    debugInfo?: null;
    carouselContent?: null;
    content?: Content | Content2 | Content3 | Content4 | Content5 | Content6 | Content7 | Content8 | null;
    attachment?: null;
    detailHeader?: null;
    leadGenFormContent?: null;
    annotation?: null;
    contextualHeader?: null;
    resharedUpdate?: null;
    interstitial?: null;
    aggregatedContent?: null;
    contextualDescription?: null;
    actor?: Actor;
    boostHeader?: null;
    '*socialDetail'?: string;
    header?: Header | null;
    highlightedComments?: any[];
    commentary?: Commentary;
    additionalContents?: any[];
    '*resharedUpdate'?: string;
    firstName?: string;
    lastName?: string;
    publicIdentifier?: string;
    profilePicture?: ProfilePicture;
    '*memberRelationship'?: string;
    headline?: string;
    name?: string;
    url?: string;
    memberRelationship?: MemberRelationship;
    hideSocialCountsIfAllowed?: boolean;
    threadUrn?: string;
    '*totalSocialActivityCounts'?: string;
    quickComments?: any[];
    socialPermissionsPersonalTopicUrn?: null;
    showPremiumAnalytics?: boolean;
    '*socialPermissions'?: string;
    feedDetailSocialActionPromptType?: null;
    allowedCommentersScope?: string;
    reactionsOnCommentsTopicUrn?: null;
    socialUpdateType?: null | string;
    reactionSummariesTopicUrn?: null | string;
    commentsTopicUrn?: null;
    reactionsTopicUrn?: null;
    reshareUpdateUrn?: null;
    socialDetailsTopicUrn?: null;
    commentSocialPermissionsTopicUrn?: null;
    messagePermission?: string;
    canShare?: boolean;
    canReact?: boolean;
    canPostComments?: boolean;
}

interface MemberRelationship {
    self: Self | null;
    '*connection'?: string;
    noConnection: null;
    connection?: null;
}

interface Self {
    __typename: string;
}

interface ProfilePicture {
    displayImageReferenceResolutionResult: DisplayImageReferenceResolutionResult;
    displayImageWithFrameReference: null;
    $recipeTypes: string[];
    displayImageUrn: string;
    $type: string;
}

interface DisplayImageReferenceResolutionResult {
    url: null;
    vectorImage: Thumbnail;
}

interface Commentary {
    numLines: number;
    translation?: null;
    navigationContext: null;
    metadataText: null;
    text: Text;
    $recipeTypes: string[];
    $type: string;
    '*translation'?: string;
}

interface Text {
    textDirection: string;
    text: string;
    attributesV2: (AttributesV24 | AttributesV222 | AttributesV232 | AttributesV242 | AttributesV25 | AttributesV26)[];
    accessibilityTextAttributesV2: any[];
    accessibilityText: null;
    $recipeTypes: string[];
    $type: string;
}

interface AttributesV26 {
    start: number;
    length: number;
    detailData: DetailData14;
    $recipeTypes: string[];
    $type: string;
}

interface DetailData14 {
    jobPostingName: null;
    hyperlink: null;
    profileFamiliarName: null;
    color: null;
    companyName: null;
    icon: null;
    epoch: null;
    systemImage: null;
    textLink: TextLink;
    listItemStyle: null;
    groupName: null;
    hyperlinkOpenExternally: null;
    listStyle: null;
    profileFullName: null;
    stringFieldReference: null;
    learningCourseName: null;
    profileMention: null;
    style: null;
    schoolName: null;
    hashtag: null;
}

interface AttributesV25 {
    start: number;
    length: number;
    detailData: DetailData13;
    $recipeTypes: string[];
    $type: string;
}

interface DetailData13 {
    jobPostingName: null;
    hyperlink: null;
    profileFamiliarName: null;
    color: null;
    companyName: null;
    '*hashtag'?: string;
    icon: null;
    epoch: null;
    systemImage: null;
    textLink: TextLink | null;
    listItemStyle: null;
    groupName: null;
    hyperlinkOpenExternally: null;
    listStyle: null;
    profileFullName: null;
    stringFieldReference: null;
    learningCourseName: null;
    profileMention: null;
    style: null;
    schoolName: null;
    hashtag?: null;
}

interface TextLink {
    viewingBehavior: string;
    controlName: null;
    sponsoredUrlAttributes: null;
    $recipeTypes: string[];
    url: string;
    $type: string;
}

interface AttributesV242 {
    start: number;
    length: number;
    detailData: DetailData12;
    $recipeTypes: string[];
    $type: string;
}

interface DetailData12 {
    jobPostingName: null;
    hyperlink: null | string;
    profileFamiliarName: null;
    color: null;
    companyName: null;
    icon: null;
    epoch: null;
    systemImage: null;
    textLink: null;
    listItemStyle: null;
    groupName: null;
    hyperlinkOpenExternally: null;
    listStyle: null;
    '*profileMention'?: string;
    profileFullName: null;
    stringFieldReference: null;
    learningCourseName: null;
    style: null;
    schoolName: null;
    hashtag?: null;
    profileMention?: null;
    '*hashtag'?: string;
}

interface AttributesV232 {
    start: number;
    length: number;
    detailData: DetailData11;
    $recipeTypes: string[];
    $type: string;
}

interface DetailData11 {
    jobPostingName: null;
    hyperlink: null | string;
    profileFamiliarName: null;
    color: null;
    companyName?: null;
    icon: null;
    epoch: null;
    systemImage: null;
    textLink: null;
    listItemStyle: null;
    groupName: null;
    hyperlinkOpenExternally: null;
    listStyle: null;
    profileFullName: null;
    stringFieldReference: null;
    learningCourseName: null;
    profileMention?: null;
    style: null | string;
    schoolName: null;
    hashtag?: null;
    '*profileMention'?: string;
    '*companyName'?: string;
    '*hashtag'?: string;
}

interface AttributesV222 {
    start: number;
    length: number;
    detailData: DetailData10;
    $recipeTypes: string[];
    $type: string;
}

interface DetailData10 {
    jobPostingName: null;
    hyperlink: null | string;
    profileFamiliarName: null;
    color: null;
    icon: null;
    epoch: null;
    systemImage: null;
    '*companyName'?: string;
    textLink: null;
    listItemStyle: null;
    groupName: null;
    hyperlinkOpenExternally: null;
    listStyle: null;
    profileFullName: null;
    stringFieldReference: null;
    learningCourseName: null;
    profileMention: null;
    style: null;
    schoolName: null;
    hashtag: null;
    companyName?: null;
}

interface AttributesV24 {
    start: number;
    length: number;
    detailData: DetailData9;
    $recipeTypes: string[];
    $type: string;
}

interface DetailData9 {
    jobPostingName: null;
    hyperlink: null | string;
    profileFamiliarName: null;
    color: null;
    companyName?: null;
    icon: null;
    epoch: null;
    systemImage: null;
    textLink: null;
    listItemStyle: null;
    groupName: null;
    hyperlinkOpenExternally: null;
    listStyle: null;
    '*profileMention'?: string;
    profileFullName: null;
    stringFieldReference: null;
    learningCourseName: null;
    style: null;
    schoolName: null;
    hashtag: null;
    profileMention?: null;
    '*companyName'?: string;
}

interface Header {
    showPremiumBranding: boolean;
    image: Image5;
    showDivider: boolean;
    actionButton: null;
    navigationContext: NavigationContext;
    headerTextStyle: string;
    text: HighlightedReactorName;
    $recipeTypes: string[];
    imageNavigationContext: NavigationContext;
    $type: string;
}

interface Image5 {
    attributes: Attribute7[];
    editableAccessibilityText: boolean;
    actionTarget: null;
    accessibilityTextAttributes: any[];
    totalCount: null;
    accessibilityText: string;
    $recipeTypes: string[];
    $type: string;
}

interface Attribute7 {
    scalingType: null;
    detailData: DetailData8;
    tintColor: null;
    $recipeTypes: string[];
    tapTargets: any[];
    displayAspectRatio: null;
    $type: string;
}

interface DetailData8 {
    profilePictureWithoutFrame: null;
    profilePictureWithRingStatus: null;
    companyLogo: null;
    icon: null;
    systemImage: null;
    nonEntityGroupLogo: null;
    vectorImage: null;
    nonEntityProfessionalEventLogo: null;
    profilePicture: null;
    imageUrl: null;
    professionalEventLogo: null;
    nonEntityCompanyLogo: null;
    nonEntitySchoolLogo: null;
    groupLogo: null;
    schoolLogo: null;
    ghostImage: null;
    nonEntityProfilePicture: NonEntityProfilePicture;
}

interface Actor {
    image: Image4;
    connectAction: null;
    supplementaryActorInfo: Description | null;
    description: Description;
    backendUrn: string;
    $recipeTypes: string[];
    followAction: null;
    $type: string;
    subDescriptionButton: null;
    name: Name;
    subDescription: SubDescription;
    navigationContext: NavigationContext;
    ringStatus: null;
    groupMembershipForJoinAction: null;
    showSmallActorPortrait: boolean;
}

interface SubDescription {
    textDirection: string;
    text: string;
    attributesV2: AttributesV23[];
    accessibilityTextAttributesV2: any[];
    accessibilityText: string;
    $recipeTypes: string[];
    $type: string;
}

interface AttributesV23 {
    start: number;
    length: number;
    detailData: DetailData7;
    $recipeTypes: string[];
    $type: string;
}

interface DetailData7 {
    jobPostingName: null;
    hyperlink: null;
    profileFamiliarName: null;
    color: null;
    companyName: null;
    icon: string;
    epoch: null;
    systemImage: null;
    textLink: null;
    listItemStyle: null;
    groupName: null;
    hyperlinkOpenExternally: null;
    listStyle: null;
    profileFullName: null;
    stringFieldReference: null;
    learningCourseName: null;
    profileMention: null;
    style: null;
    schoolName: null;
    hashtag: null;
}

interface Name {
    textDirection: string;
    text: string;
    attributesV2: AttributesV22[];
    accessibilityTextAttributesV2: AttributesV22[];
    accessibilityText: string;
    $recipeTypes: string[];
    $type: string;
}

interface AttributesV22 {
    start: number;
    length: number;
    detailData: DetailData6;
    $recipeTypes: string[];
    $type: string;
}

interface DetailData6 {
    jobPostingName: null;
    hyperlink: null;
    profileFamiliarName: null;
    color: null;
    companyName?: null;
    '*profileFullName'?: string;
    icon: null;
    epoch: null;
    systemImage: null;
    textLink: null;
    listItemStyle: null;
    groupName: null;
    hyperlinkOpenExternally: null;
    listStyle: null;
    stringFieldReference: null;
    learningCourseName: null;
    profileMention: null;
    style: null;
    schoolName: null;
    hashtag: null;
    '*companyName'?: string;
    profileFullName?: null;
}

interface Image4 {
    attributes: Attribute6[];
    editableAccessibilityText: boolean;
    actionTarget: null;
    accessibilityTextAttributes: any[];
    totalCount: null;
    accessibilityText: null;
    $recipeTypes: string[];
    $type: string;
}

interface Attribute6 {
    scalingType: null;
    detailData: DetailData5;
    tintColor: null;
    $recipeTypes: string[];
    tapTargets: any[];
    displayAspectRatio: null;
    $type: string;
}

interface DetailData5 {
    profilePictureWithoutFrame: null;
    profilePictureWithRingStatus: null;
    companyLogo: null;
    icon: null;
    systemImage: null;
    nonEntityGroupLogo: null;
    vectorImage: null;
    nonEntityProfessionalEventLogo: null;
    profilePicture: null;
    imageUrl: null;
    professionalEventLogo: null;
    nonEntityCompanyLogo: NonEntityCompanyLogo | null;
    nonEntitySchoolLogo: null;
    groupLogo: null;
    schoolLogo: null;
    ghostImage: null;
    nonEntityProfilePicture: NonEntityProfilePicture | null;
}

interface NonEntityProfilePicture {
    '*profile': string;
    ringStatus: null;
    $recipeTypes: string[];
    vectorImage: Thumbnail;
    $type: string;
}

interface NonEntityCompanyLogo {
    '*company': string;
    $recipeTypes: string[];
    vectorImage: Thumbnail;
    $type: string;
}

interface Content8 {
    toggleButtonComponent: null;
    feedDividerComponent: null;
    articleComponent: ArticleComponent3;
    eventComponent: null;
    conversationsComponent: null;
    promoComponent: null;
    seeMoreComponent: null;
    reviewComponent: null;
    feedDiscoveryGridComponent: null;
    gentlePromptComponent: null;
    followPromptComponent: null;
    jobComponent: null;
    learningRecommendationComponent: null;
    miniUpdateContentComponent: null;
    buttonComponent: null;
    surveyComponent: null;
    actorComponent: null;
    celebrationComponent: null;
    documentComponent: null;
    creationStatusComponent: null;
    dynamicPollComponent: null;
    newsletterComponent: null;
    postCtaComponent: null;
    scheduledLiveContentComponent: null;
    entityComponent: null;
    externalVideoComponent: null;
    videoCarouselItemComponent: null;
    linkedInVideoComponent: null;
    textOverlayImageComponent: null;
    annotationComponent: null;
    detailedSurveyComponent: null;
    imageComponent: null;
    showcaseComponent: null;
    pollComponent: null;
    feedDiscoveryEntityComponent: null;
    promptComponent: null;
    contextualActionComponent: null;
    announcementComponent: null;
    commentaryComponent: null;
    miniUpdateCommentaryComponent: null;
    slideshowComponent: null;
    callToActionComponent: null;
}

interface ArticleComponent3 {
    headlineBackgroundColor: string;
    newsletterNavigationContext: null;
    largeImage: null;
    smallImage: SmallImage;
    subdescription: null;
    description: null;
    video: null;
    type: string;
    title: Headline;
    articleUrn: string;
    animatedOverlay: null;
    swapTitleAndSubtitle: boolean;
    showChevron: boolean;
    subscribeAction: null;
    showSmallTitle: boolean;
    byline: null;
    inlineCta: null;
    bylineImage: null;
    newsletterLogo: null;
    $recipeTypes: string[];
    $type: string;
    newsletterTitle: null;
    subtitle: Headline;
    navigationContext: NavigationContext;
}

interface SmallImage {
    attributes: Attribute5[];
    editableAccessibilityText: boolean;
    actionTarget: null;
    accessibilityTextAttributes: any[];
    totalCount: null;
    accessibilityText: null;
    $recipeTypes: string[];
    $type: string;
}

interface Attribute5 {
    scalingType: string;
    detailData: DetailData3;
    tintColor: null;
    $recipeTypes: string[];
    tapTargets: any[];
    displayAspectRatio: number;
    $type: string;
}

interface Content7 {
    toggleButtonComponent: null;
    feedDividerComponent: null;
    articleComponent: null;
    eventComponent: null;
    conversationsComponent: null;
    promoComponent: null;
    seeMoreComponent: null;
    reviewComponent: null;
    feedDiscoveryGridComponent: null;
    gentlePromptComponent: null;
    followPromptComponent: null;
    jobComponent: null;
    learningRecommendationComponent: null;
    miniUpdateContentComponent: null;
    buttonComponent: null;
    surveyComponent: null;
    actorComponent: null;
    celebrationComponent: null;
    documentComponent: null;
    creationStatusComponent: null;
    dynamicPollComponent: null;
    newsletterComponent: null;
    postCtaComponent: null;
    scheduledLiveContentComponent: null;
    entityComponent: null;
    externalVideoComponent: null;
    videoCarouselItemComponent: null;
    linkedInVideoComponent: null;
    textOverlayImageComponent: null;
    annotationComponent: null;
    imageComponent: ImageComponent2;
    detailedSurveyComponent: null;
    showcaseComponent: null;
    pollComponent: null;
    feedDiscoveryEntityComponent: null;
    promptComponent: null;
    contextualActionComponent: null;
    announcementComponent: null;
    commentaryComponent: null;
    miniUpdateCommentaryComponent: null;
    slideshowComponent: null;
    callToActionComponent: null;
}

interface ImageComponent2 {
    images: Image3[];
    largeCtaButton: null;
    multiphotoRenderingFormat: string;
    navigationContext: null;
    showTemplateCta: boolean;
    $recipeTypes: string[];
    $type: string;
}

interface Image3 {
    attributes: Attribute4[];
    editableAccessibilityText: boolean;
    actionTarget: null;
    accessibilityTextAttributes: any[];
    totalCount: null;
    accessibilityText: string;
    $recipeTypes: string[];
    $type: string;
}

interface Attribute4 {
    scalingType: string;
    detailData: DetailData4;
    tintColor: null;
    $recipeTypes: string[];
    tapTargets: any[];
    displayAspectRatio: number;
    $type: string;
}

interface Content6 {
    toggleButtonComponent: null;
    feedDividerComponent: null;
    articleComponent: null;
    eventComponent: null;
    conversationsComponent: null;
    promoComponent: null;
    seeMoreComponent: null;
    reviewComponent: null;
    feedDiscoveryGridComponent: null;
    gentlePromptComponent: null;
    followPromptComponent: null;
    jobComponent: null;
    learningRecommendationComponent: null;
    miniUpdateContentComponent: null;
    buttonComponent: null;
    surveyComponent: null;
    actorComponent: null;
    celebrationComponent: null;
    documentComponent: DocumentComponent;
    creationStatusComponent: null;
    dynamicPollComponent: null;
    newsletterComponent: null;
    postCtaComponent: null;
    scheduledLiveContentComponent: null;
    entityComponent: null;
    externalVideoComponent: null;
    videoCarouselItemComponent: null;
    linkedInVideoComponent: null;
    textOverlayImageComponent: null;
    annotationComponent: null;
    detailedSurveyComponent: null;
    imageComponent: null;
    showcaseComponent: null;
    pollComponent: null;
    feedDiscoveryEntityComponent: null;
    promptComponent: null;
    contextualActionComponent: null;
    announcementComponent: null;
    commentaryComponent: null;
    miniUpdateCommentaryComponent: null;
    slideshowComponent: null;
    callToActionComponent: null;
}

interface DocumentComponent {
    ctaButton: null;
    document: Document;
    showDownloadCTA: boolean;
    $recipeTypes: string[];
    documentSource: null;
    $type: string;
}

interface Document {
    transcribedDocumentUrl: string;
    manifestUrlExpiresAt: number;
    title: string;
    $recipeTypes: string[];
    $type: string;
    urn: string;
    sliced: boolean;
    manifestUrl: string;
    scanRequiredForDownload: boolean;
    totalPageCount: number;
    coverPages: CoverPages;
    fullDocumentPageCount: null;
    transcribedDocumentUrlExpiresAt: number;
}

interface CoverPages {
    pagesPerResolution: PagesPerResolution[];
    transcripts: any[];
    $recipeTypes: string[];
    $type: string;
}

interface PagesPerResolution {
    width: number;
    $recipeTypes: string[];
    imageUrls: string[];
    height: number;
    $type: string;
}

interface Content5 {
    toggleButtonComponent: null;
    feedDividerComponent: null;
    articleComponent: ArticleComponent2;
    eventComponent: null;
    conversationsComponent: null;
    promoComponent: null;
    seeMoreComponent: null;
    reviewComponent: null;
    feedDiscoveryGridComponent: null;
    gentlePromptComponent: null;
    followPromptComponent: null;
    jobComponent: null;
    learningRecommendationComponent: null;
    miniUpdateContentComponent: null;
    buttonComponent: null;
    surveyComponent: null;
    actorComponent: null;
    celebrationComponent: null;
    documentComponent: null;
    creationStatusComponent: null;
    dynamicPollComponent: null;
    newsletterComponent: null;
    postCtaComponent: null;
    scheduledLiveContentComponent: null;
    entityComponent: null;
    externalVideoComponent: null;
    videoCarouselItemComponent: null;
    linkedInVideoComponent: null;
    textOverlayImageComponent: null;
    annotationComponent: null;
    detailedSurveyComponent: null;
    imageComponent: null;
    showcaseComponent: null;
    pollComponent: null;
    feedDiscoveryEntityComponent: null;
    promptComponent: null;
    contextualActionComponent: null;
    announcementComponent: null;
    commentaryComponent: null;
    miniUpdateCommentaryComponent: null;
    slideshowComponent: null;
    callToActionComponent: null;
}

interface ArticleComponent2 {
    headlineBackgroundColor: string;
    newsletterNavigationContext: null;
    largeImage: null;
    smallImage: null;
    subdescription: null;
    description: null;
    video: null;
    type: string;
    title: Headline;
    articleUrn: string;
    animatedOverlay: null;
    swapTitleAndSubtitle: boolean;
    showChevron: boolean;
    subscribeAction: null;
    showSmallTitle: boolean;
    byline: null;
    inlineCta: null;
    bylineImage: null;
    newsletterLogo: null;
    $recipeTypes: string[];
    $type: string;
    newsletterTitle: null;
    subtitle: Headline;
    navigationContext: NavigationContext;
}

interface Content4 {
    toggleButtonComponent: null;
    feedDividerComponent: null;
    articleComponent: null;
    eventComponent: null;
    conversationsComponent: null;
    promoComponent: null;
    seeMoreComponent: null;
    reviewComponent: null;
    feedDiscoveryGridComponent: null;
    gentlePromptComponent: null;
    followPromptComponent: null;
    jobComponent: null;
    learningRecommendationComponent: null;
    miniUpdateContentComponent: null;
    buttonComponent: null;
    surveyComponent: null;
    actorComponent: null;
    celebrationComponent: null;
    documentComponent: null;
    creationStatusComponent: null;
    dynamicPollComponent: null;
    newsletterComponent: null;
    postCtaComponent: null;
    scheduledLiveContentComponent: null;
    entityComponent: null;
    externalVideoComponent: null;
    videoCarouselItemComponent: null;
    linkedInVideoComponent: LinkedInVideoComponent;
    textOverlayImageComponent: null;
    annotationComponent: null;
    detailedSurveyComponent: null;
    imageComponent: null;
    showcaseComponent: null;
    pollComponent: null;
    feedDiscoveryEntityComponent: null;
    promptComponent: null;
    contextualActionComponent: null;
    announcementComponent: null;
    commentaryComponent: null;
    miniUpdateCommentaryComponent: null;
    slideshowComponent: null;
    callToActionComponent: null;
}

interface LinkedInVideoComponent {
    headlineBackgroundColor: string;
    embedUrl: null;
    headlineAnimation: string;
    bannerType: null;
    viewerState: null;
    concurrentViewerCountTopicUrn: null;
    emitPromoTracking: null;
    concurrentViewerCount: null;
    title: null;
    titleContext: null;
    shouldDisplayLiveIndicator: boolean;
    swapTitleAndSubtitle: boolean;
    sponsoredVideoCompletionCta: null;
    inStreamAds: null;
    insightImage: null;
    showSmallTitle: boolean;
    viewerTrackingTopicUrn: null;
    tapTargets: any[];
    videoPreviewMetadata: null;
    overlayTexts: any[];
    preEventRegistrationImage: null;
    largeCtaButton: null;
    descriptionContainerNavigationContext: null;
    trimOffsetEnd: null;
    inlineCtaButton: null;
    containerAspectRatio: null;
    trimOffsetStart: null;
    $recipeTypes: string[];
    sponsoredLandingUrlViewingBehavior: string;
    $type: string;
    autoTranscriptEnabled: null;
    editableVideoAttributes: null;
    subtitle: null;
    '*saveState': string;
    navigationContext: null;
    sponsoredLandingUrl: null;
    autoTranscriptToggleable: null;
    insightText: null;
    '*videoPlayMetadata': string;
}

interface Content3 {
    toggleButtonComponent: null;
    feedDividerComponent: null;
    articleComponent: null;
    eventComponent: null;
    conversationsComponent: null;
    promoComponent: null;
    seeMoreComponent: null;
    reviewComponent: null;
    feedDiscoveryGridComponent: null;
    gentlePromptComponent: null;
    followPromptComponent: null;
    jobComponent: null;
    learningRecommendationComponent: null;
    miniUpdateContentComponent: null;
    buttonComponent: null;
    surveyComponent: null;
    actorComponent: null;
    celebrationComponent: null;
    documentComponent: null;
    creationStatusComponent: null;
    dynamicPollComponent: null;
    newsletterComponent: null;
    postCtaComponent: null;
    scheduledLiveContentComponent: null;
    entityComponent: null;
    externalVideoComponent: null;
    videoCarouselItemComponent: null;
    linkedInVideoComponent: null;
    textOverlayImageComponent: null;
    annotationComponent: null;
    imageComponent: ImageComponent;
    detailedSurveyComponent: null;
    showcaseComponent: null;
    pollComponent: null;
    feedDiscoveryEntityComponent: null;
    promptComponent: null;
    contextualActionComponent: null;
    announcementComponent: null;
    commentaryComponent: null;
    miniUpdateCommentaryComponent: null;
    slideshowComponent: null;
    callToActionComponent: null;
}

interface ImageComponent {
    images: Image2[];
    largeCtaButton: null;
    multiphotoRenderingFormat: string;
    navigationContext: null;
    showTemplateCta: boolean;
    $recipeTypes: string[];
    $type: string;
}

interface Image2 {
    attributes: Attribute3[];
    editableAccessibilityText: boolean;
    actionTarget: null;
    accessibilityTextAttributes: any[];
    totalCount: null;
    accessibilityText: string;
    $recipeTypes: string[];
    $type: string;
}

interface Attribute3 {
    scalingType: string;
    detailData: DetailData4;
    tintColor: null;
    $recipeTypes: string[];
    tapTargets: TapTarget[];
    displayAspectRatio: number;
    $type: string;
}

interface TapTarget {
    firstCornerXOffsetPercentage: number;
    type: string;
    $recipeTypes: string[];
    thirdCornerYOffsetPercentage: number;
    url: string;
    $type: string;
    urn: string;
    thirdCornerXOffsetPercentage: number;
    secondCornerYOffsetPercentage: number;
    fourthCornerXOffsetPercentage: number;
    tapTargetEntity: TapTargetEntity;
    firstCornerYOffsetPercentage: number;
    rank: number;
    untaggable: boolean;
    fourthCornerYOffsetPercentage: number;
    text: string;
    secondCornerXOffsetPercentage: number;
}

interface TapTargetEntity {
    companyUrn: null;
    '*profileUrn': string;
}

interface DetailData4 {
    profilePictureWithoutFrame: null;
    profilePictureWithRingStatus: null;
    companyLogo: null;
    icon: null;
    systemImage: null;
    nonEntityGroupLogo: null;
    vectorImage: VectorImage;
    nonEntityProfessionalEventLogo: null;
    profilePicture: null;
    imageUrl: null;
    professionalEventLogo: null;
    nonEntityCompanyLogo: null;
    nonEntitySchoolLogo: null;
    groupLogo: null;
    schoolLogo: null;
    ghostImage: null;
    nonEntityProfilePicture: null;
}

interface VectorImage {
    digitalmediaAsset: string;
    attribution: null;
    c2paManifestData: null;
    $recipeTypes: string[];
    artifacts: Artifact[];
    rootUrl: string;
    $type: string;
}

interface Content2 {
    toggleButtonComponent: null;
    feedDividerComponent: null;
    articleComponent: ArticleComponent;
    eventComponent: null;
    conversationsComponent: null;
    promoComponent: null;
    seeMoreComponent: null;
    reviewComponent: null;
    feedDiscoveryGridComponent: null;
    gentlePromptComponent: null;
    followPromptComponent: null;
    jobComponent: null;
    learningRecommendationComponent: null;
    miniUpdateContentComponent: null;
    buttonComponent: null;
    surveyComponent: null;
    actorComponent: null;
    celebrationComponent: null;
    documentComponent: null;
    creationStatusComponent: null;
    dynamicPollComponent: null;
    newsletterComponent: null;
    postCtaComponent: null;
    scheduledLiveContentComponent: null;
    entityComponent: null;
    externalVideoComponent: null;
    videoCarouselItemComponent: null;
    linkedInVideoComponent: null;
    textOverlayImageComponent: null;
    annotationComponent: null;
    detailedSurveyComponent: null;
    imageComponent: null;
    showcaseComponent: null;
    pollComponent: null;
    feedDiscoveryEntityComponent: null;
    promptComponent: null;
    contextualActionComponent: null;
    announcementComponent: null;
    commentaryComponent: null;
    miniUpdateCommentaryComponent: null;
    slideshowComponent: null;
    callToActionComponent: null;
}

interface ArticleComponent {
    headlineBackgroundColor: string;
    newsletterNavigationContext: null;
    largeImage: LargeImage;
    smallImage: null;
    subdescription: null;
    description: Description;
    video: null;
    type: string;
    title: Headline;
    articleUrn: string;
    animatedOverlay: null;
    swapTitleAndSubtitle: boolean;
    showChevron: boolean;
    subscribeAction: null;
    showSmallTitle: boolean;
    byline: null;
    inlineCta: null;
    bylineImage: null;
    newsletterLogo: null;
    $recipeTypes: string[];
    $type: string;
    newsletterTitle: null;
    subtitle: Headline;
    navigationContext: NavigationContext;
}

interface NavigationContext {
    actionTarget: string;
    urlViewingBehavior: string;
    trackingActionType: string;
    accessibilityText: string;
    sponsoredUrlAttributes: null;
    $recipeTypes: string[];
    $type: string;
}

interface Description {
    textDirection: string;
    text: string;
    attributesV2: any[];
    accessibilityTextAttributesV2: any[];
    accessibilityText: string;
    $recipeTypes: string[];
    $type: string;
}

interface LargeImage {
    attributes: Attribute2[];
    editableAccessibilityText: boolean;
    actionTarget: null;
    accessibilityTextAttributes: any[];
    totalCount: null;
    accessibilityText: null;
    $recipeTypes: string[];
    $type: string;
}

interface Attribute2 {
    scalingType: string;
    detailData: DetailData3;
    tintColor: null;
    $recipeTypes: string[];
    tapTargets: any[];
    displayAspectRatio: null;
    $type: string;
}

interface DetailData3 {
    profilePictureWithoutFrame: null;
    profilePictureWithRingStatus: null;
    companyLogo: null;
    icon: null;
    systemImage: null;
    nonEntityGroupLogo: null;
    vectorImage: Thumbnail;
    nonEntityProfessionalEventLogo: null;
    profilePicture: null;
    imageUrl: null;
    professionalEventLogo: null;
    nonEntityCompanyLogo: null;
    nonEntitySchoolLogo: null;
    groupLogo: null;
    schoolLogo: null;
    ghostImage: null;
    nonEntityProfilePicture: null;
}

interface Content {
    toggleButtonComponent: null;
    feedDividerComponent: null;
    articleComponent: null;
    eventComponent: null;
    conversationsComponent: null;
    promoComponent: null;
    seeMoreComponent: null;
    reviewComponent: null;
    feedDiscoveryGridComponent: null;
    gentlePromptComponent: null;
    followPromptComponent: null;
    jobComponent: null;
    learningRecommendationComponent: null;
    miniUpdateContentComponent: null;
    buttonComponent: null;
    surveyComponent: null;
    celebrationComponent: CelebrationComponent;
    actorComponent: null;
    documentComponent: null;
    creationStatusComponent: null;
    dynamicPollComponent: null;
    newsletterComponent: null;
    postCtaComponent: null;
    scheduledLiveContentComponent: null;
    entityComponent: null;
    externalVideoComponent: null;
    videoCarouselItemComponent: null;
    linkedInVideoComponent: null;
    textOverlayImageComponent: null;
    annotationComponent: null;
    detailedSurveyComponent: null;
    imageComponent: null;
    showcaseComponent: null;
    pollComponent: null;
    feedDiscoveryEntityComponent: null;
    promptComponent: null;
    contextualActionComponent: null;
    announcementComponent: null;
    commentaryComponent: null;
    miniUpdateCommentaryComponent: null;
    slideshowComponent: null;
    callToActionComponent: null;
}

interface CelebrationComponent {
    highlightedMessage: null;
    image: Image;
    headline: Headline;
    $recipeTypes: string[];
    $type: string;
}

interface Headline {
    textDirection: string;
    text: string;
    attributesV2: any[];
    accessibilityTextAttributesV2: any[];
    accessibilityText: null;
    $recipeTypes: string[];
    $type: string;
}

interface Image {
    attributes: Attribute[];
    editableAccessibilityText: boolean;
    actionTarget: null;
    accessibilityTextAttributes: any[];
    totalCount: null;
    accessibilityText: null;
    $recipeTypes: string[];
    $type: string;
}

interface Attribute {
    scalingType: null;
    detailData: DetailData2;
    tintColor: null;
    $recipeTypes: string[];
    tapTargets: any[];
    displayAspectRatio: null;
    $type: string;
}

interface DetailData2 {
    profilePictureWithoutFrame: null;
    profilePictureWithRingStatus: null;
    companyLogo: null;
    icon: null;
    systemImage: null;
    nonEntityGroupLogo: null;
    vectorImage: null;
    nonEntityProfessionalEventLogo: null;
    profilePicture: null;
    imageUrl: ImageUrl;
    professionalEventLogo: null;
    nonEntityCompanyLogo: null;
    nonEntitySchoolLogo: null;
    groupLogo: null;
    schoolLogo: null;
    ghostImage: null;
    nonEntityProfilePicture: null;
}

interface ImageUrl {
    originalImageUrl: null;
    originalHeight: null;
    $recipeTypes: string[];
    url: string;
    originalWidth: null;
    $type: string;
}

interface SocialContent {
    updateSaveState: null;
    hideCommentsCount: boolean;
    hideReactionsCount: boolean;
    hideSocialActivityCounts: boolean;
    hideShareAction: boolean;
    hideSendAction: boolean;
    $recipeTypes: string[];
    hideRepostsCount: boolean;
    $type: string;
    hideViewsCount: boolean;
    hideReactAction: boolean;
    coachAction: null;
    hideCommentAction: boolean;
    shareUrl: string;
    showContributionExperience: boolean;
}

interface Metadata2 {
    actionsPosition: string;
    shareMediaUrn: null;
    actionTriggerEnabled: boolean;
    coachSummaryText?: null;
    shareAudience: string;
    legoTrackingToken: null;
    backendUrn: string;
    coachSummaryUrn?: null;
    $recipeTypes: string[];
    trackingData: TrackingData;
    $type: string;
    '*updateActions': string;
    detailPageType: string;
    shouldForceRefetchFromNetwork: boolean;
    shareUrn: string;
    excludedFromSeen: boolean;
    coachAction?: null;
    rootShare: boolean;
    hidePostAction: null;
    group: null;
}

interface TrackingData {
    sponsoredTracking: null;
    requestId: string;
    backendUrn: null;
    $recipeTypes: string[];
    trackingId: string;
    $type: string;
}

interface ReactionTypeCount {
    count: number;
    reactionType: string;
    $recipeTypes: string[];
    $type: string;
}

interface HighlightedReactorName {
    textDirection: string;
    text: string;
    attributesV2: AttributesV2[];
    accessibilityTextAttributesV2: any[];
    accessibilityText: null;
    $recipeTypes: string[];
    $type: string;
}

interface AttributesV2 {
    start: number;
    length: number;
    detailData: DetailData;
    $recipeTypes: string[];
    $type: string;
}

interface DetailData {
    jobPostingName: null;
    hyperlink: null;
    profileFamiliarName: null;
    color: null;
    companyName: null;
    '*profileFullName': string;
    icon: null;
    epoch: null;
    systemImage: null;
    textLink: null;
    listItemStyle: null;
    groupName: null;
    hyperlinkOpenExternally: null;
    listStyle: null;
    stringFieldReference: null;
    learningCourseName: null;
    profileMention: null;
    style: null;
    schoolName: null;
    hashtag: null;
}

interface AdaptiveStream {
    protocol: string;
    masterPlaylists: StreamingLocation[];
    initialBitRate: number;
    mediaType: string;
    $recipeTypes: string[];
    $type: string;
}

interface ProgressiveStream {
    streamingLocations: StreamingLocation[];
    size: number;
    bitRate: number;
    width: number;
    mediaType: string;
    mimeType: null;
    $recipeTypes: string[];
    height: number;
    $type: string;
}

interface StreamingLocation {
    $recipeTypes: string[];
    url: string;
    expiresAt: number;
    $type: string;
}

interface Thumbnail {
    digitalmediaAsset: null;
    attribution: null;
    c2paManifestData: null;
    $recipeTypes: string[];
    artifacts: Artifact[];
    rootUrl: string;
    $type: string;
}

interface Artifact {
    width: number;
    $recipeTypes: string[];
    fileIdentifyingUrlPathSegment: string;
    expiresAt: number;
    height: number;
    $type: string;
}

interface Data2 {
    data: Data;
}

interface Data {
    $recipeTypes: string[];
    feedDashProfileUpdatesByMemberShareFeed: FeedDashProfileUpdatesByMemberShareFeed;
    $type: string;
}

interface FeedDashProfileUpdatesByMemberShareFeed {
    metadata: Metadata;
    paging: Paging;
    '*elements': string[];
    $recipeTypes: string[];
    $type: string;
}

interface Paging {
    count: number;
    start: number;
    total: number;
    $recipeTypes: string[];
    $type: string;
}

interface Metadata {
    paginationToken: string;
    paginationTokenExpiryTime: null;
    $recipeTypes: string[];
    $type: string;
}
